import { ChangeDetectionStrategy, Component, HostListener, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, map, startWith } from 'rxjs';
import {
  Bell,
  Check,
  CheckCheck,
  LucideAngularModule,
  LucideIconData,
  Menu,
  Moon,
  Sparkles,
  Sun,
} from 'lucide-angular';

import { BrandingService } from '../../core/branding/branding.service';
import { AppNotification } from '../../core/models/notification.model';
import { NotificationStateService } from '../../core/services/notification-state.service';
import { ThemeService } from '../../core/services/theme.service';
import { OfficeMarkComponent } from '../../shared/components/office-mark/office-mark.component';
import { LayoutService } from '../layout.service';

@Component({
  selector: 'app-topbar',
  imports: [LucideAngularModule, OfficeMarkComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <header class="topbar">
      <div class="topbar-left">
        <button
          type="button"
          class="topbar-btn menu-trigger"
          aria-label="Abrir menu principal"
          aria-controls="app-navigation"
          [attr.aria-expanded]="layout.navOpen()"
          (click)="openNav()">
          <lucide-icon [img]="menuIcon" [size]="19" aria-hidden="true"></lucide-icon>
        </button>

        <div class="topbar-brand">
          <app-office-mark />
          <strong>{{ branding.displayName() }}</strong>
        </div>

        @if (pageTitle()) {
          <span class="topbar-page-title">{{ pageTitle() }}</span>
        }
      </div>

      <div class="topbar-actions">
        <button
          type="button"
          class="topbar-btn"
          [attr.aria-label]="theme.isDark() ? 'Ativar tema claro' : 'Ativar tema escuro'"
          [attr.title]="theme.isDark() ? 'Tema claro' : 'Tema escuro'"
          (click)="theme.toggle()">
          <lucide-icon [img]="theme.isDark() ? sunIcon : moonIcon" [size]="17" aria-hidden="true"></lucide-icon>
        </button>

        <button
          type="button"
          class="topbar-btn"
          [class.open]="notificationsOpen()"
          [attr.aria-label]="'Notificações' + (unreadNotifications() > 0 ? ', ' + unreadNotifications() + ' novas' : '')"
          [attr.aria-expanded]="notificationsOpen()"
          (click)="toggleNotifications()">
          <lucide-icon [img]="bellIcon" [size]="18" aria-hidden="true"></lucide-icon>
          @if (unreadNotifications() > 0) {
            <span class="notif-dot" aria-hidden="true"></span>
          }
        </button>
      </div>
    </header>

    <div class="overlay" [class.open]="notificationsOpen()" aria-hidden="true" (click)="closePanels()"></div>

    @if (notificationsOpen()) {
      <section class="notif-panel" role="dialog" aria-label="Notificações" aria-modal="true">
        <div class="notif-panel-header">
          <div>
            <p class="notif-kicker">Notificações</p>
            <h2>Atualizações recentes</h2>
          </div>
          <span class="notif-chip">
            {{ unreadNotifications() > 0 ? unreadNotifications() + ' novas' : 'Tudo em dia' }}
          </span>
        </div>

        @if (unreadNotifications() > 0) {
          <button type="button" class="notif-mark-all" (click)="markAllRead()">
            <lucide-icon [img]="checkAllIcon" [size]="14" aria-hidden="true"></lucide-icon>
            Marcar todas como lidas
          </button>
        }

        @if (notificationState.loading()) {
          <div class="notif-empty">
            <div class="notif-empty-icon">
              <lucide-icon [img]="bellIcon" [size]="18" aria-hidden="true"></lucide-icon>
            </div>
            <div class="notif-empty-copy">
              <strong>Carregando notificações...</strong>
            </div>
          </div>
        } @else if (notificationItems().length === 0) {
          <div class="notif-empty">
            <div class="notif-empty-icon">
              <lucide-icon [img]="sparklesIcon" [size]="18" aria-hidden="true"></lucide-icon>
            </div>
            <div class="notif-empty-copy">
              <strong>Sem novidades no momento</strong>
              <p>Você será avisado quando houver algo importante.</p>
            </div>
          </div>
        } @else {
          <ul class="notif-list">
            @for (item of notificationItems(); track item.public_id) {
              <li>
                <button type="button" class="notif-item" [class.unread]="!item.read_at" (click)="openNotification(item)">
                  <span class="notif-item-dot" aria-hidden="true"></span>
                  <div class="notif-item-body">
                    <strong>{{ item.title }}</strong>
                    <p>{{ item.message }}</p>
                    <small>{{ formatRelativeTime(item.created_at) }}</small>
                  </div>
                  @if (!item.read_at) {
                    <span class="notif-item-mark" (click)="markRead($event, item)" aria-label="Marcar como lida">
                      <lucide-icon [img]="checkIcon" [size]="13" aria-hidden="true"></lucide-icon>
                    </span>
                  }
                </button>
              </li>
            }
          </ul>
        }
      </section>
    }
  `,
  styles: `
    /* ── Barra ───────────────────────────────────────────── */
    .topbar {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      z-index: 60;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.75rem;
      height: calc(var(--topbar-height) + env(safe-area-inset-top, 0px));
      padding: env(safe-area-inset-top, 0px) 1rem 0;
      border-bottom: 1px solid var(--color-border);
      background: color-mix(in srgb, var(--color-surface) 92%, transparent);
      backdrop-filter: blur(16px);
      -webkit-backdrop-filter: blur(16px);
    }

    .topbar-left {
      display: flex;
      align-items: center;
      gap: 0.6rem;
      min-width: 0;
    }

    .topbar-brand {
      display: flex;
      align-items: center;
      gap: 0.55rem;
      min-width: 0;
    }

    .topbar-brand app-office-mark { --mark-size: 28px; }

    .topbar-brand strong {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-family: var(--font-display);
      font-size: 1.125rem;
      font-weight: 600;
      letter-spacing: -0.01em;
      color: var(--color-text-primary);
    }

    .topbar-page-title {
      display: none;
      font-size: 0.875rem;
      font-weight: 500;
      color: var(--color-text-secondary);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .topbar-actions {
      display: flex;
      align-items: center;
      gap: 0.4rem;
      flex-shrink: 0;
    }

    .topbar-btn {
      position: relative;
      display: grid;
      place-items: center;
      width: 40px;
      height: 40px;
      border: 1px solid var(--color-border);
      border-radius: var(--radius-sm);
      background: var(--color-surface);
      color: var(--color-text-secondary);
      cursor: pointer;
      transition: background var(--transition-fast), border-color var(--transition-fast), color var(--transition-fast);
      -webkit-tap-highlight-color: transparent;
    }

    .topbar-btn:hover,
    .topbar-btn.open {
      background: var(--color-soft-surface);
      border-color: var(--color-border-strong);
      color: var(--color-text-primary);
    }

    .topbar-btn:focus-visible {
      outline: 2px solid var(--color-primary);
      outline-offset: 2px;
    }

    .notif-dot {
      position: absolute;
      top: 8px;
      right: 8px;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: var(--color-primary);
      border: 2px solid var(--color-surface);
    }

    /* ── Painel de notificações ──────────────────────────── */
    .overlay {
      position: fixed;
      inset: 0;
      z-index: 120;
      background: rgba(8, 14, 26, 0.35);
      opacity: 0;
      pointer-events: none;
      transition: opacity var(--transition-base);
    }

    .overlay.open {
      opacity: 1;
      pointer-events: auto;
    }

    .notif-panel {
      position: fixed;
      top: calc(var(--topbar-height) + env(safe-area-inset-top, 0px) + 0.5rem);
      right: 0.75rem;
      left: 0.75rem;
      z-index: 122;
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
      padding: 1rem;
      background: var(--color-surface);
      border: 1px solid var(--color-border);
      border-radius: var(--radius-lg);
      box-shadow: var(--shadow-lg);
      animation: scaleIn 150ms ease;
    }

    .notif-panel-header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 0.75rem;
    }

    .notif-kicker {
      margin: 0 0 0.2rem;
      font-size: 0.6875rem;
      font-weight: 600;
      color: var(--color-text-muted);
      text-transform: uppercase;
      letter-spacing: 0.08em;
    }

    .notif-panel-header h2 {
      margin: 0;
      font-size: 1.125rem;
      color: var(--color-text-primary);
    }

    .notif-chip {
      flex-shrink: 0;
      padding: 0.2rem 0.6rem;
      border-radius: var(--radius-full);
      background: var(--color-primary-subtle);
      color: var(--color-primary);
      font-size: 0.6875rem;
      font-weight: 600;
      white-space: nowrap;
    }

    .notif-empty {
      display: flex;
      align-items: flex-start;
      gap: 0.75rem;
      padding: 0.875rem;
      border-radius: var(--radius-sm);
      border: 1px solid var(--color-border-subtle);
      background: var(--color-soft-surface);
    }

    .notif-empty-icon {
      width: 36px;
      height: 36px;
      flex-shrink: 0;
      display: grid;
      place-items: center;
      border-radius: var(--radius-sm);
      background: var(--color-primary-subtle);
      color: var(--color-primary);
    }

    .notif-empty-copy strong {
      display: block;
      margin-bottom: 0.2rem;
      font-size: 0.875rem;
      font-weight: 600;
      color: var(--color-text-primary);
    }

    .notif-empty-copy p {
      margin: 0;
      font-size: 0.8125rem;
      line-height: 1.5;
      color: var(--color-text-muted);
    }

    .notif-mark-all {
      align-self: flex-start;
      display: flex;
      align-items: center;
      gap: 0.4rem;
      padding: 0.25rem 0;
      border: none;
      background: transparent;
      color: var(--color-primary);
      font-size: 0.8125rem;
      font-weight: 600;
      cursor: pointer;
    }

    .notif-mark-all:hover { text-decoration: underline; }

    .notif-list {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      max-height: min(60vh, 420px);
      overflow-y: auto;
    }

    .notif-item {
      display: flex;
      align-items: flex-start;
      gap: 0.6rem;
      width: 100%;
      padding: 0.6rem;
      border: 1px solid transparent;
      border-radius: var(--radius-sm);
      background: transparent;
      text-align: left;
      cursor: pointer;
      transition: background var(--transition-fast);
    }

    .notif-item:hover { background: var(--color-soft-surface); }
    .notif-item.unread { background: var(--color-primary-subtle); }

    .notif-item-dot {
      width: 7px;
      height: 7px;
      margin-top: 0.4rem;
      flex-shrink: 0;
      border-radius: 50%;
      background: var(--color-primary);
      opacity: 0;
    }

    .notif-item.unread .notif-item-dot { opacity: 1; }

    .notif-item-body {
      flex: 1;
      min-width: 0;
      display: grid;
      gap: 0.15rem;
    }

    .notif-item-body strong {
      font-size: 0.8125rem;
      font-weight: 600;
      color: var(--color-text-primary);
    }

    .notif-item-body p {
      margin: 0;
      font-size: 0.78125rem;
      line-height: 1.4;
      color: var(--color-text-secondary);
    }

    .notif-item-body small {
      font-size: 0.6875rem;
      color: var(--color-text-muted);
    }

    .notif-item-mark {
      width: 24px;
      height: 24px;
      flex-shrink: 0;
      display: grid;
      place-items: center;
      border-radius: 50%;
      color: var(--color-text-muted);
      cursor: pointer;
    }

    .notif-item-mark:hover {
      background: var(--color-soft-surface);
      color: var(--color-primary);
    }

    /* ── Desktop ─────────────────────────────────────────── */
    @media (min-width: 1024px) {
      .topbar {
        left: var(--sidebar-width);
        padding-inline: 1.5rem;
      }

      .menu-trigger,
      .topbar-brand { display: none; }

      .topbar-page-title { display: block; }

      .notif-panel {
        left: auto;
        right: 1.5rem;
        width: min(100%, 22rem);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .notif-panel { animation: none; }
      .overlay { transition: none; }
    }
  `,
})
export class TopbarComponent {
  protected readonly theme = inject(ThemeService);
  protected readonly layout = inject(LayoutService);
  protected readonly branding = inject(BrandingService);
  protected readonly notificationState = inject(NotificationStateService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);

  protected readonly pageTitle = toSignal(
    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      startWith(null),
      map(() => {
        let route = this.activatedRoute.snapshot;
        while (route.firstChild) route = route.firstChild;
        return (route.data['title'] as string) ?? '';
      }),
    ),
    { initialValue: '' },
  );

  readonly menuIcon: LucideIconData = Menu;
  readonly bellIcon: LucideIconData = Bell;
  readonly sparklesIcon: LucideIconData = Sparkles;
  readonly checkIcon: LucideIconData = Check;
  readonly checkAllIcon: LucideIconData = CheckCheck;
  readonly sunIcon: LucideIconData = Sun;
  readonly moonIcon: LucideIconData = Moon;

  protected readonly notificationsOpen = signal(false);
  protected readonly unreadNotifications = computed(() => this.notificationState.unreadCount());
  protected readonly notificationItems = computed(() => this.notificationState.items());

  constructor() {
    this.notificationState.init();
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    this.closePanels();
  }

  protected openNav(): void {
    this.notificationsOpen.set(false);
    this.layout.openNav();
  }

  protected toggleNotifications(): void {
    const next = !this.notificationsOpen();
    this.notificationsOpen.set(next);
    if (next) this.layout.closeNav();
  }

  protected closePanels(): void {
    this.notificationsOpen.set(false);
  }

  protected markAllRead(): void {
    this.notificationState.markAllRead();
  }

  protected markRead(event: Event, item: AppNotification): void {
    event.stopPropagation();
    if (!item.read_at) {
      this.notificationState.markRead(item.public_id);
    }
  }

  protected openNotification(item: AppNotification): void {
    if (!item.read_at) {
      this.notificationState.markRead(item.public_id);
    }
    this.closePanels();

    if (item.entity === 'tasks' && item.entity_public_id) {
      void this.router.navigateByUrl(`/app/tasks/${item.entity_public_id}`);
    }
  }

  protected formatRelativeTime(isoDate: string): string {
    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    const diffMs = Date.now() - date.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);

    if (diffMinutes < 1) return 'agora mesmo';
    if (diffMinutes < 60) return `há ${diffMinutes} min`;

    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `há ${diffHours} h`;

    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `há ${diffDays} d`;

    return new Intl.DateTimeFormat('pt-BR').format(date);
  }
}
